import { CreditCardLegalDocument } from './data/graphql';

// Mobile and tablet breakpoints
export const TABLET_BREAKPOINT = '53.125rem'; // 850px
export const MOBILE_BREAKPOINT = '31.25rem'; // 500px
export const SMALL_MOBILE_BREAKPOINT = '21.875rem'; // 350px

// Main content card breakpoint, fit to content card size, to lose grey spacing between nav and card
// also used for prosper and partner bank logo resizing
export const CARD_BREAKPOINT = '40rem'; // 640px

// Shape Design System - color styles
const utilityColors = {
  GREY_1: '#303030',
  GREY_2: '#4D4D4D',
  GREY_3: '#727272',
  GREY_4: '#909090',
  GREY_5: '#C6C6C6',
  GREY_6: '#E6E6E6',
  GREY_7: '#F8F8F8',
  WHITE: '#FFFFFF',
  RED_1: '#E30707',
  RED_2: '#FDF4F4',
  YELLOW_1: '#A96200',
  YELLOW_2: '#EDA30F',
  YELLOW_3: '#FFF9E8',
  TEAL_1: '#007F86',
  TEAL_2: '#009C9B',
  TEAL_3: '#F0FBFB',
  GREEN_1: '#148235',
  GREEN_2: '#319C07',
  GREEN_3: '#F3FFF0',
  ORANGE_1: '#DA6000',
  ORANGE_2: '#F06900',
  ORANGE_3: '#FFF6EC',
  PROSPER_ORANGE: '#B85100',
};

// Common colors across project
// ------------------------------------------------------------------------------------------------
// UI_PRIMARY               main theme color of interactive elements
// UI_PRIMARY_ACTION        hover/focus color of UI_PRIMARY
// UI_PRIMARY_ACCENT        lighter UI_PRIMARY used for icons and other nontext UI
// UI_SECONDARY             color of secondary interactive elements, e.g. non-CTA buttons
// UI_SECONDARY_ACTION      selected color of UI_SECONDARY
// UI_OUTLINE               teal to indicate focus on some interactive elements
// BORDER_PRIMARY           medium gray used for some interactive element borders
// BORDER_SECONDARY         light gray used for UI_SECONDARY and separating blocks of text
// TEXT_PRIMARY             gray used for fonts on white background
// TEXT_ACTION              orange used for fonts on white background suggesting a CTA
// TEXT_SECONDARY           orange used for fonts on white background
// TEXT_INPUT               dark grey used for text input values
// TEXT_ERROR               red used to show validation error borders and icons
// TEXT_ERROR_ACCENT        light pink background color for TEXT_ERROR red
// TEXT_LABEL               lighter gray font color of placeholder text, labels, etc.

// TODO: Map remaining hex values to named color values in utilityColors
const accessibleColors = {
  UI_PRIMARY: utilityColors.ORANGE_2,
  UI_PRIMARY_ACTION: '#D55D00',
  UI_PRIMARY_ACCENT: '#FE7800',
  UI_SECONDARY_ACCENT: '#D65E00',
  UI_SECONDARY: '#F8F8F8',
  UI_SECONDARY_ACTION: '#FFF7F0',
  UI_OUTLINE: utilityColors.TEAL_2,
  BORDER_PRIMARY: utilityColors.GREY_5,
  BORDER_SECONDARY: utilityColors.GREY_6,
  BORDER_TERTIARY: utilityColors.GREY_4,
  TEXT_PRIMARY: utilityColors.GREY_2,
  TEXT_ACTION: '#BD5300',
  TEXT_SECONDARY: '#616161',
  TEXT_INPUT: utilityColors.GREY_1,
  TEXT_ERROR: utilityColors.RED_1,
  TEXT_ERROR_ACCENT: utilityColors.RED_2,
  TEXT_LABEL: utilityColors.GREY_3,
  TEXT_ANCHOR: utilityColors.PROSPER_ORANGE,
  DROPDOWN_BACKGROUND: utilityColors.WHITE,
  DROPDOWN_HOVER: utilityColors.GREY_7,
  BUTTON_PRIMARY: utilityColors.ORANGE_2,
  BUTTON_PRIMARY_TEXT: utilityColors.WHITE,
  BUTTON_PRIMARY_HOVER: utilityColors.ORANGE_1,
  BUTTON_SECONDARY_TEXT: utilityColors.ORANGE_1,
  BUTTON_SECONDARY_HOVER: utilityColors.ORANGE_3,
};

export const colors = accessibleColors;

export enum DeviceType {
  DESKTOP = 'DESKTOP',
  MOBILE_WEB = 'MOBILE_WEB',
}

export const MOBILE_WIDTH = parseFloat(MOBILE_BREAKPOINT) * 16;

export const STATES = [
  '',
  'AK',
  'AL',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

export const DATES = [
  '',
  '1st',
  '2nd',
  '3rd',
  '4th',
  '5th',
  '6th',
  '7th',
  '8th',
  '9th',
  '10th',
  '11th',
  '12th',
  '13th',
  '14th',
  '15th',
  '16th',
  '17th',
  '18th',
  '19th',
  '20th',
  '21st',
  '22nd',
  '23rd',
  '24th',
  '25th',
  '26th',
  '27th',
  '28th',
];

export const INCOME_SOURCE_TYPES = [
  {
    label: 'Employment',
    enum: 'EMPLOYED',
  },
  {
    label: 'Self-employment',
    enum: 'SELF_EMPLOYED',
  },
  {
    label: 'Retirement',
    enum: 'RETIRED',
  },
  {
    label: 'Unemployment',
    enum: 'UNEMPLOYED',
  },
  {
    label: 'Other',
    enum: 'OTHER',
  },
];

// Maps income sources to income types that the backend expects
export const INCOME_SOURCE_MAP = new Map([
  ['EMPLOYED', 'EMPLOYED'],
  ['SELF_EMPLOYED', 'SELF_EMPLOYED'],
  ['RETIRED', 'OTHER'],
  ['UNEMPLOYED', 'OTHER'],
  ['OTHER', 'OTHER'],
]);

export enum FunnelType {
  LONG_FORM = 'LONG_FORM',
  LONG_FORM_DSA_INDIRECT = 'LONG_FORM_DSA_INDIRECT',
  LONG_FORM_DSA_DIRECT = 'LONG_FORM_DSA_DIRECT',
  STEP_BY_STEP = 'STEP_BY_STEP',
}

export type CreateApplicationPayload = {
  applicant: Applicant;
  legalDocumentsToSave: CreditCardLegalDocument[];
  campaignRefCode: string;
  deviceType?: DeviceType;
  offerCode: string | null;
  funnelType?: FunnelType;
  isDsaEnrolled?: boolean;
};

export type BankConnectionAuthorizationLegalDocument = {
  name: string;
  title: string;
  version: number;
  viewUrl: string;
};

export type SelectedBankAccount = {
  maskedAccountNumber?: string;
  bankName?: string;
  plaidInstitutionId?: string;
};

export type OfferCheck = {
  offer?: {
    creditLimit?: string;
    conditionalOnCashflowVerification?: boolean;
    creditApr?: string;
  };
};

export type ApplicationType = {
  applicationId: string;
  applicationStatus?: ApplicationStatus;
  externalUserId: string;
  hasConsentedToTcpa: boolean;
  applicant: Applicant;
  offerCheck?: OfferCheck;
  selectedBankAccount: SelectedBankAccount;
  autopayPreferences?: CreditCardApplicationAutopayPreferences;
  plaidLinkToken: string;
  applicationAttribution?: {
    campaignRefCode?: string;
    funnelType?: FunnelType;
  };
};

export type ApplicationContextType = {
  creditCardApplication: ApplicationType;
  fetchCreditCardApplication: Function;
  fetchAndResumeCreditCardApplication: Function;
  fetchingApplication: boolean;
  showApplicationFetchError: boolean;
  showVerificationWaitingRoom: boolean;
  setShowVerificationWaitingRoom: Function;
  pollCreditCardApplication: Function;
  pollingApplication: boolean;
  showPollingError: boolean;
  onSuperscriptClick: Function;
  onSuperscriptEnter: Function;
};

type Address = {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
};

export type Applicant = {
  applicantId?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  ssn?: string;
  address?: Address;
  mailingAddress?: Address;
  income: {
    incomeSourceTypes: Array<string>;
    annualIncome: string;
    additionalIncome: string | null;
    housingPayment: string;
  };
};

export type CreditCardApplicationAutopayPreferences = {
  paymentAmountType: CreditCardApplicationAutopayPreferencesPaymentAmountType;
  monthlyPaymentDay: number;
};

enum CreditCardApplicationAutopayPreferencesPaymentAmountType {
  MINIMUM_PAYMENT = 'MINIMUM_PAYMENT',
  STATEMENT_BALANCE = 'STATEMENT_BALANCE',
  NO_AUTOPAY = 'NO_AUTOPAY',
}

// Application workflow event enum
export enum ApplicationWorkflowEvent {
  OFFER_RETRIED = 'OFFER_RETRIED',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
  BANK_CONNECT_AUTHORIZED = 'BANK_CONNECT_AUTHORIZED',
  BANK_CONNECTED = 'BANK_CONNECTED',
  VERIFICATION_RETRIED = 'VERIFICATION_RETRIED',
  EXPORT_RETRIED = 'EXPORT_RETRIED',
  APPLICATION_SUBMITTED = 'APPLICATION_SUBMITTED',
  REJECTION_RETRIED = 'REJECTION_RETRIED',
  DEVICE_VALIDATED = 'DEVICE_VALIDATED',
  SSN_REENTERED = 'SSN_REENTERED',
}

// Application status enum
export enum ApplicationStatus {
  OFFER_IN_PROGRESS = 'OFFER_IN_PROGRESS',
  OFFER_ERROR = 'OFFER_ERROR',
  PENDING_OFFER_ACCEPT = 'PENDING_OFFER_ACCEPT',
  PENDING_BANK_CONNECT_AUTHORIZATION = 'PENDING_BANK_CONNECT_AUTHORIZATION',
  PENDING_BANK_CONNECT = 'PENDING_BANK_CONNECT',
  VERIFICATION_IN_PROGRESS = 'VERIFICATION_IN_PROGRESS',
  VERIFICATION_ERROR = 'VERIFICATION_ERROR',
  PENDING_SUBMISSION = 'PENDING_SUBMISSION',
  EXPORT_IN_PROGRESS = 'EXPORT_IN_PROGRESS',
  EXPORT_ERROR = 'EXPORT_ERROR',
  APPLICATION_COMPLETED = 'APPLICATION_COMPLETED',
  REJECTION_IN_PROGRESS = 'REJECTION_IN_PROGRESS',
  REJECTION_ERROR = 'REJECTION_ERROR',
  APPLICATION_REJECTED = 'APPLICATION_REJECTED',
  APPLICATION_WITHDRAWN = 'APPLICATION_WITHDRAWN',
  UNCONDITIONAL_VERIFICATION_IN_PROGRESS = 'UNCONDITIONAL_VERIFICATION_IN_PROGRESS',
  UNCONDITIONAL_VERIFICATION_ERROR = 'UNCONDITIONAL_VERIFICATION_ERROR',
  PENDING_UNCONDITIONAL_OFFER_ACCEPT = 'PENDING_UNCONDITIONAL_OFFER_ACCEPT',
  PENDING_UNCONDITIONAL_SUBMISSION = 'PENDING_UNCONDITIONAL_SUBMISSION',
  PENDING_UNCONDITIONAL_DEVICE_VALIDATION = 'PENDING_UNCONDITIONAL_DEVICE_VALIDATION',
  PENDING_CONDITIONAL_DEVICE_VALIDATION = 'PENDING_CONDITIONAL_DEVICE_VALIDATION',
  PENDING_SSN_REENTRY = 'PENDING_SSN_REENTRY',
}

export enum PageNames {
  APPLY = 'APPLY_PAGE',
  OFFER_WAITING_ROOM = 'OFFER_WAITING_ROOM',
  OFFER = 'OFFER',
  BANK_CONNECT = 'BANK_CONNECT',
  VERIFICATION_WAITING_ROOM = 'VERIFICATION_WAITING_ROOM',
  VERIFICATION_EMAIL = 'VERIFICATION_EMAIL',
  AUTO_PAY = 'AUTO_PAY',
  PAYMENT_DATE = 'PAYMENT_DATE',
  EXPORT_WAITING_ROOM = 'EXPORT_WAITING_ROOM',
  FINISH = 'FINISH',
  DECLINE = 'DECLINE',
  AGREEMENTS = 'AGREEMENTS',
}

export enum LegalDocuments {
  COASTAL_COMMUNITY_BANK_PRIVACY_POLICY = 'COASTAL_COMMUNITY_BANK_PRIVACY_POLICY',
  PROSPER_PRIVACY_POLICY = 'PROSPER_PRIVACY_POLICY',
  TERMS_OF_USE_AND_ELECTRONIC_CONSENT = 'TERMS_OF_USE_AND_ELECTRONIC_CONSENT',
  TCPA_AUTHORIZATION = 'TCPA_AUTHORIZATION',
  TCPA_AUTHORIZATION_HTML = 'TCPA_AUTHORIZATION_HTML',
  DATA_TERMS_OF_USE = 'DATA_TERMS_OF_USE',
  CREDIT_REPORT_AUTHORIZATION = 'CREDIT_REPORT_AUTHORIZATION',
  TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
  BANK_CONNECTION_AUTHORIZATION = 'BANK_CONNECTION_AUTHORIZATION',
  CARDHOLDER_AGREEMENT = 'CARDHOLDER_AGREEMENT',
  PROSPER_CARD_CARDHOLDER_AGREEMENT = 'PROSPER_CARD_CARDHOLDER_AGREEMENT',
}

export enum LegalDocumentContexts {
  BANK_CONNECTION = 'BANK_CONNECTION',
  DIRECT_APP_REGISTRATION = 'DIRECT_APP_REGISTRATION',
  AUTOPAY_PREFERENCE = 'AUTOPAY_PREFERENCE',
}

// Local storage keys
export const JWT_KEY = 'prosper-jwt';
export const PLAID_LINK_TOKEN_KEY = 'plaid-link-token';
export const LANDING_PAGE_OFFER_CODE_KEY = 'offerCode';
export const CC_APPLICATION_IDENTITY = 'cc-application-identity';

// Use as the href attribute for an anchor element for contacting support
export const SUPPORT_PHONE_NUMBER_HREF = 'tel:+1-800-851-0630';

// App store and Play store redirect links take you to the Cardhub detail page
export const GOOGLE_PLAYSTORE_APP_LINK =
  'https://play.google.com/store/apps/details?id=com.prosper.cardhub';
export const APPLE_APPSTORE_APP_LINK =
  'https://apps.apple.com/us/app/my-prosper-card/id1588303199';

// Cardhub web servicing URLs
export const FISERV_SIGNIN_PROD_URL =
  'https://www.prospercards.com/ch/login/?appToken=prospercardhubsdk0721&fiToken=1218PROSPERCH';
export const FISERV_SIGNIN_DEV_URL =
  'https://cardhub-cat2.fiservapps.com/ch/login/?appToken=prospercardhubsdk0721&fiToken=1218PROSPERCH';

// Coastal Bank Privacy Policy link
export const COASTAL_BANK_PRIVACY_POLICY_URL =
  'https://www.coastalbank.com/privacy-notice.html';

// Use either PROD or DEV Cardhub web servicing URL depending on current environment
export const FISERV_SIGNIN_URL =
  window.location.hostname === 'www.prosper.com'
    ? FISERV_SIGNIN_PROD_URL
    : FISERV_SIGNIN_DEV_URL;

// Array of currently accepted partner crc substrings
export const PARTNER_CAMPAIGN_CODES = [
  'CK',
  'RV-CCDC',
  'RV-BR',
  'WH',
  'EXPERIAN',
  'ITMEDIA',
  'BESTMONEY',
  'MONEYLION',
  'FIONA',
  'LT',
  'EQUIFAX',
  'CREDITSESAME',
  'NAV',
];

// Redirect link to the Credit Karma NPC reviews
export const CK_REVIEW_LINK =
  'https://www.creditkarma.com/reviews/credit-card/single/id/CCProsper01';

// AmOne base link without AFFID query parameter
export const AMONE_DECLINE_BASE_LINK =
  'http://o1.qnsr.com/cgi/r?;n=203;c=1680785;s=32476;x=7936;f=202209081330350;u=j;z=TIMESTAMP;';

// AmOne AFFID query parameter values denote possible decline types
enum AmOneAffid {
  OFFER_DECLINED = 'ProsperCardAppDecline',
  TWO_STEP_VERIFICATION_FAILED = 'ProsperCard2StepDecline',
  ONE_STEP_VERIFICATION_FAILED = 'ProsperCard1StepDecline',
}

// Determine AmOne AFFID query parameter value using the offerCheck on the application
const AMONE_AFFID_BY_DECLINE_TYPE = (offerCheck: OfferCheck) => {
  // Get the offer on the application to determine the decline type
  const { offer } = offerCheck;
  // Check if application was one-step or two-step when an offer credit limit exists
  if (offer !== null && offer.creditLimit) {
    // Indicate one-step or two-step based on the conditionalOnCashflowVerification property
    return offer.conditionalOnCashflowVerification
      ? AmOneAffid.TWO_STEP_VERIFICATION_FAILED
      : AmOneAffid.ONE_STEP_VERIFICATION_FAILED;
  }
  // Indicate the application was declined for an offer when it has no credit limit
  return AmOneAffid.OFFER_DECLINED;
};

// Construct AmOne link with AFFID to use on the Decline page
export const AMONE_DECLINE_URL = (offerCheck: OfferCheck) => {
  const affidValue = AMONE_AFFID_BY_DECLINE_TYPE(offerCheck);
  const affidQuery = `AFFID=${affidValue}`;
  const amOneDeclineLink = `${AMONE_DECLINE_BASE_LINK}${affidQuery}`;
  return amOneDeclineLink;
};

// Optimizely feature flags & experiment names
export const BANK_BLOCKLIST_OPTIMIZELY_KEY = 'npc_bank_blocklist';
export const GQL_FEDERATION_FEATURE_OPTIMIZELY_KEY = 'npc-federation-gateway';
export const GQL_FEDERATION_QUERIES_AND_MUTATIONS_FEATURE_VARIABLE_OPTIMIZELY_KEY =
  'queriesAndMutationsSupportedByFederation';
export const SKIP_TMX_CLIENT_SIDE_SCRIPT_OPTIMIZELY_KEY =
  'skip-npc-tmx-client-side-script';
export const DISABLE_ADDRESS_AUTOCOMPLETE_OPTIMIZELY_KEY =
  'npc-disable-address-autocomplete';
export const SET_PAYMENT_DATE_OPTIMIZELY_KEY = 'npc-set-payment-date';
export const NPC_FUNNEL_AUTOPAY_TWO_KEY = 'npc-funnel-autopay-2';
export const NPC_FUNNEL_DSA_ONE_KEY = 'npc-funnel-dsa-1';
export const EXIT_BANNER_OPTIMIZELY_KEY = 'npc-exit-banner';
export const SHOW_CK_REVIEW_LINK_OPTIMIZELY_KEY = 'npc-show-ck-review-link';
export const PREPOP_UPDATE_INCOME_KEY = 'npc-prepop-update-income';
export const APPLY_FLOW_IDENTITY_EMAIL = 'npc-apply-flow-identity-email';
export const GREY_OUT_FN_LN_CRC = 'npc-grey-out-fn-ln-using-crc';
export const NPC_UDPATE_EXISTING_PROSPER = 'npc_update_existing_prosper';
export const CARDSTAR_OVERRIDE_ACCOUNT_CREATION =
  'cardstar_override_account_creation';
export const NPC_FUNNEL_ENABLE_UPDATED_ANNUAL_FEE =
  'npc-funnel-enable-updated-annual-fee';
export const NPC_FUNNEL_TCPA = 'npc-funnel-tcpa';

// Optimizely vendor name for svc-experiment-tracking
export const OPTIMIZELY_VENDOR_NAME = 'Optimizely';

// Error message for signaling unauthorized access
export const UNAUTHORIZED_MESSAGE = 'UNAUTHORIZED';

// Error message we expect when fetching an app the user doesn't own
export const ENTITY_NOT_FOUND_MESSAGE = 'entity_not_found';

// Possible values from GraphQLResponse.errors.code
export enum GraphQLErrorCodes {
  INVALID_WORKFLOW_EVENT_FOR_STATUS = 'invalid_workflow_event_for_status',
  TERMINAL_APPLICATION = 'terminal_application_is_locked_for_modification',
  PLAID_SELECTED_ACCOUNT_NOT_FOUND = 'plaid_selected_account_not_found',
}

export const PROSPER_DM_OFFER_CODE_CAMPAIGN_CODE = '-OC-CC';

export const CARD_SERVICING_PATHS = {
  dashboard: '/card/servicing/home',
  welcome: '/card/servicing',
  signin: '/card/servicing/sign_in',
};
